import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FiMenu, FiLogOut, FiChevronDown, FiExternalLink } from 'react-icons/fi';
import { NAV_GROUPS, NAV_ITEMS, findNavItem } from '../../routes';
import { useUserData } from '../Hooks/useUserData';
import { FindCookieValue, GetBaseURL } from '../../Utils';
import { CookieTypes } from '../../Models';
import { initialsOf } from '../../Utils/Format';
import Logo from '../../assets/nft-sm-logo.png';

const AppShell: React.FC = ({ children }) => {
  const location = useLocation();
  const { userData, removeUserAfterLogout } = useUserData();
  const [navOpen, setNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const groups: string[] = userData.currentUser.security_groups || [];
  const name = (userData.currentUser.full_name || FindCookieValue(CookieTypes.FullName) || '') as string;
  const email = (userData.currentUser.email || FindCookieValue(CookieTypes.Email) || '') as string;
  const current = findNavItem(location.pathname);

  useEffect(() => {
    setNavOpen(false);
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    document.title = current ? `${current.label} · No Fuss Tutors Admin` : 'No Fuss Tutors Admin';
  }, [current]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const logout = async () => {
    try {
      const response = await fetch(`${GetBaseURL()}/api/login/logout`);
      if (response.status !== 200) return;
    } catch (err) {
      // fall through: clear the local session even if the API is unreachable
    }
    removeUserAfterLogout();
  };

  const visible = NAV_ITEMS.filter((item) => groups.length === 0 || groups.includes(item.securityGroup));

  return (
    <div className="app-shell">
      <aside className={`sidebar${navOpen ? ' is-open' : ''}`} aria-label="Main navigation">
        <Link to="/admin" className="sidebar__brand">
          <img src={Logo} alt="" />
          <div>
            No Fuss Tutors
            <small>Admin</small>
          </div>
        </Link>
        <div className="sidebar__scroll">
          {NAV_GROUPS.map((group) => {
            const items = visible.filter((item) => item.group === group);
            if (items.length === 0) return null;
            return (
              <div className="sidebar__group" key={group}>
                {group !== 'Overview' && <div className="sidebar__group-title">{group}</div>}
                {items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    exact={item.path === '/admin'}
                    className="sidebar__link"
                    activeClassName="is-active"
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>
        <div className="sidebar__footer">
          <a href="https://nofusstutors.com" target="_blank" rel="noopener noreferrer" className="sidebar__link" style={{ padding: 0, height: '3rem' }}>
            <FiExternalLink />
            nofusstutors.com
          </a>
        </div>
      </aside>
      <div className={`sidebar-backdrop${navOpen ? ' is-open' : ''}`} onClick={() => setNavOpen(false)} />
      <div className="app-main">
        <header className="topbar">
          <button className="btn--ghost btn--icon topbar__menu" onClick={() => setNavOpen(true)} aria-label="Open navigation">
            <FiMenu />
          </button>
          <div className="topbar__crumbs">
            {current && current.group !== current.label && (
              <>
                <span>{current.group}</span>
                <span aria-hidden="true">/</span>
              </>
            )}
            <strong>{current ? current.label : 'Dashboard'}</strong>
          </div>
          <div className="topbar__spacer" />
          <div className="topbar__user" ref={menuRef}>
            <button className="user-chip" onClick={() => setMenuOpen((open) => !open)} aria-haspopup="menu" aria-expanded={menuOpen}>
              <span className="avatar">{initialsOf(name, email)}</span>
              <span>{name || 'Account'}</span>
              <FiChevronDown />
            </button>
            {menuOpen && (
              <div className="menu" role="menu">
                <div className="menu__header">
                  <strong>{name}</strong>
                  <span>{email}</span>
                </div>
                <button className="menu__item is-danger" role="menuitem" onClick={logout}>
                  <FiLogOut /> Log out
                </button>
              </div>
            )}
          </div>
        </header>
        <div className="app-content">{children}</div>
      </div>
    </div>
  );
};

export default AppShell;
